import React, { useEffect, useRef, useState, useCallback } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./OccasionEdit.css";

/* assets */
const occasionsBg = "/assets/bg2.jpeg";
const babyShower = "/assets/babyShower.jpeg";
const wedding = "/assets/wedding.jpeg";
const engagement = "/assets/engagement.jpeg";
const haldi = "/assets/haldi.jpeg";
const poojaRitual = "/assets/poojaRitual.jpeg";
const mehendi = "/assets/mehendi.jpeg";
const festival = "/assets/festival.jpeg";
const sareeFunction = "/assets/sareefunction.jpeg";

function OccasionCard({ img, title, blurb, i = 0, onClick = () => {}, isMobile = false, isActive = false }) {
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(title);
    }
  };

  const handleClick = () => {
    onClick(title);
  };

  if (isMobile) {
    return (
      <div
        className={`mobile-carousel-card ${isActive ? 'active' : ''}`}
        role="listitem"
        data-aos="fade-up"
        data-aos-delay={80 + i * 60}
        data-aos-offset="120"
      >
        <div
          className="mobile-card__media"
          role="button"
          tabIndex={0}
          aria-label={`Open ${title} occasion`}
          onClick={handleClick}
          onKeyDown={handleKey}
        >
          <img 
            className="mobile-card__img" 
            src={img} 
            alt={`${title} occasion collection showcasing traditional Indian sarees`}
            loading="lazy" 
            decoding="async"
            width="300"
            height="375"
          />
          <div className="mobile-card__grad" aria-hidden="true" />
          <div className="mobile-card__text">
            <h3 className="mobile-card__title">{title}</h3>
            <p className="mobile-card__blurb">{blurb}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <article
      className="c-card"
      role="listitem"
      data-aos="fade-up"
      data-aos-delay={80 + i * 60}
      data-aos-offset="120"
    >
      <div
        className="c-card__media"
        role="button"
        tabIndex={0}
        aria-label={`Open ${title}`}
        onClick={handleClick}
        onKeyDown={handleKey}
      >
        <img 
          className="c-card__img" 
          src={img} 
          alt={`${title} occasion collection showcasing traditional Indian sarees`}
          loading="lazy" 
          decoding="async"
          width="300"
          height="375"
        />
        <div className="c-card__grad" aria-hidden="true" />
        <div className="c-card__text">
          <h3 className="ty-title-3">{title}</h3>
          <p className="ty-body-2">{blurb}</p>
        </div>
      </div>
    </article>
  );
}

export default function OccasionEdit({
  onNavigateToCollections = () => {},
}) {
  // Handle occasion-specific navigation
  const handleOccasionClick = (occasionTitle) => {
    // Always redirect to collections page
    onNavigateToCollections();
  };
  // Mobile carousel state
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const viewportRef = useRef(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    try {
      // Check if user prefers reduced motion
      const prefersReducedMotion = window.matchMedia && 
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      
      // Only initialize AOS if it's available
      if (typeof AOS !== 'undefined' && AOS) {
        AOS.init({ 
          duration: prefersReducedMotion ? 0 : 700, 
          easing: "ease-out-quart", 
          once: true, 
          offset: 80,
          disable: () => prefersReducedMotion,
        });
      }

      // Check if mobile
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    } catch (error) {
      console.error('Error in OccasionEdit useEffect:', error);
    }
  }, []);

  const collectionsOccasion = [
    { img: babyShower,   title: "Baby Shower",    blurb: "Delicate designs for the joyful occasion." },
    { img: wedding,      title: "Wedding",        blurb: "Grand wedding sarees to mark the most important day." },
    { img: engagement,   title: "Engagement",     blurb: "Elegant sarees for your memorable engagement." },
    { img: haldi,        title: "Haldi",          blurb: "Traditional styles for the festive Haldi ceremony." },
    { img: poojaRitual,  title: "Pooja & Ritual", blurb: "Sarees for auspicious rituals and ceremonies." },
    { img: mehendi,      title: "Mehendi",        blurb: "Vibrant and beautiful designs for your mehendi function." },
    { img: festival,     title: "Festival",       blurb: "Celebrate in bright and bold festive sarees." },
    { img: sareeFunction,title: "Saree Function", blurb: "Traditional sarees for your special saree function." },
  ];

  // Mobile carousel functions
  const goToSlide = useCallback((slideIndex) => {
    setCurrentSlide(slideIndex);
    if (viewportRef.current) {
      viewportRef.current.scrollTo({
        left: slideIndex * viewportRef.current.clientWidth,
        behavior: "smooth",
      });
    }
  }, []);

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % collectionsOccasion.length;
    goToSlide(next);
  }, [currentSlide, collectionsOccasion.length, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = currentSlide === 0 ? collectionsOccasion.length - 1 : currentSlide - 1;
    goToSlide(prev);
  }, [currentSlide, collectionsOccasion.length, goToSlide]);

  // Auto-play for mobile carousel
  useEffect(() => {
    if (!isMobile) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isMobile, nextSlide]);

  return (
    <section
      className="occasions-parallax"
      style={{ backgroundImage: `url(${occasionsBg})` }}
      aria-label="Occasion Edit"
    >
      <div className="occasions-inner">
        <header className="c-head c-head--onImage" data-aos="fade-up" data-aos-delay="60">
          <h2 className="c-h2 c-h2--onImage ty-title-2">Occasion Edit</h2>
          <p className="c-sub c-sub--onImage ty-body-2">
            Shop by moment — weddings, cocktails, and festivities.
          </p>
        </header>

        {isMobile ? (
          <div className="mobile-carousel-container">
            <div 
              ref={sliderRef}
              className="mobile-carousel-slider"
              role="region"
              aria-label="Occasions carousel"
            >
              <button
                className="mobile-carousel-arrow prev"
                aria-label="Previous occasion"
                onClick={prevSlide}
              >
                ‹
              </button>
              <button
                className="mobile-carousel-arrow next"
                aria-label="Next occasion"
                onClick={nextSlide}
              >
                ›
              </button>

              <div ref={viewportRef} className="mobile-carousel-viewport">
                <div className="mobile-carousel-track" role="list">
                  {collectionsOccasion.map((c, i) => (
                    <OccasionCard
                      key={c.title}
                      {...c}
                      i={i}
                      isMobile={true}
                      isActive={i === currentSlide}
                      onClick={() => handleOccasionClick(c.title)}
                    />
                  ))}
                </div>
              </div>

              <div className="mobile-carousel-dots" role="tablist" aria-label="Occasion pages">
                {collectionsOccasion.map((_, idx) => (
                  <button
                    key={idx}
                    className={`mobile-carousel-dot ${idx === currentSlide ? "active" : ""}`}
                    role="tab"
                    aria-selected={idx === currentSlide}
                    aria-label={`Go to ${collectionsOccasion[idx].title} occasion`}
                    onClick={() => goToSlide(idx)}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="c-gridWrap c-gridWrap--onImage">
            <div className="c-grid" role="list">
              {collectionsOccasion.map((c, i) => (
                <OccasionCard
                  key={c.title}
                  {...c}
                  i={i}
                  onClick={() => onNavigateToCollections()}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      
      
    </section>
    
  );
}
